import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiGrid, FiPlus, FiEdit2, FiTrash2, FiLogOut, FiStar,
  FiExternalLink, FiPackage, FiUsers, FiTrendingUp, FiX, FiCheck,
  FiInbox, FiRefreshCw, FiMail, FiCalendar, FiTag, FiDollarSign
} from 'react-icons/fi';
import { useApp } from '../../context/AppContext';
import { fetchMessages } from '../../context/AppContext';

const emptyForm = {
  title: '', description: '', category: 'Web Development',
  technologies: '', thumbnail: '', liveUrl: '', githubUrl: '', featured: false,
  features: '',
};

const AdminDashboard = () => {
  const { adminUser, logout, projects, addProject, updateProject, deleteProject } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const loadMessages = useCallback(async () => {
    setLoadingMessages(true);
    try {
      const msgs = await fetchMessages();
      setMessages(msgs);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'messages') loadMessages();
  }, [activeTab, loadMessages]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditingProject(null);
    setShowModal(true);
  };

  const openEdit = (project) => {
    setForm({
      ...project,
      technologies: project.technologies.join(', '),
      features: (project.features || []).join(', '),
    });
    setEditingProject(project.id);
    setShowModal(true);
  };

  const handleSave = () => {
    const data = {
      ...form,
      technologies: form.technologies.split(',').map(t => t.trim()).filter(Boolean),
      features: form.features.split(',').map(f => f.trim()).filter(Boolean),
      gallery: [form.thumbnail],
    };
    if (editingProject) updateProject(editingProject, data);
    else addProject(data);
    setShowModal(false);
  };

  const categories = ['Web Development', 'Web App Development', 'UI/UX Design', 'Branding', 'Software Development'];

  const stats = [
    { icon: <FiPackage />, label: 'Total Projects', value: projects.length },
    { icon: <FiStar />, label: 'Featured', value: projects.filter(p => p.featured).length },
    { icon: <FiUsers />, label: 'Categories', value: [...new Set(projects.map(p => p.category))].length },
    { icon: <FiInbox />, label: 'Messages', value: messages.length },
  ];

  const navItems = [
    { id: 'projects', icon: <FiGrid />, label: 'Projects' },
    { id: 'messages', icon: <FiInbox />, label: 'Messages' },
    { id: 'add', icon: <FiPlus />, label: 'Add Project', action: openAdd },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 border-r border-white/5 flex flex-col"
        style={{ background: 'rgba(13,8,16,0.97)' }}>
        <div className="p-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #7f049b, #1c0c19)' }}>
              <span className="text-white font-display font-bold text-sm">N</span>
            </div>
            <span className="font-display text-lg text-white tracking-widest"> SitePixel</span>
          </Link>
          <p className="font-body text-xs text-white/30 mt-2 tracking-wide">Admin Panel</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={item.action || (() => setActiveTab(item.id))}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm transition-all duration-200 ${
                activeTab === item.id && !item.action
                  ? 'text-white bg-purple-500/18 border border-purple-500/28'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="text-base">{item.icon}</span>
              {item.label}
              {item.id === 'messages' && messages.length > 0 && (
                <span className="ml-auto text-xs bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded-full">
                  {messages.length}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-500/20 text-purple-400 text-sm font-body font-medium">
              {adminUser?.username?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-body text-xs text-white">{adminUser?.username}</p>
              <p className="font-body text-xs text-white/30">Administrator</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl font-body text-sm text-white/50 hover:text-white hover:bg-white/5 transition-all duration-200"
          >
            <FiLogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1 className="font-display text-4xl font-normal text-white">
                {activeTab === 'messages' ? 'Messages' : 'Dashboard'}
              </h1>
              <p className="font-body text-sm text-white/40 mt-1">
                {activeTab === 'messages'
                  ? 'Contact form submissions from your website'
                  : 'Manage your projects and content'}
              </p>
            </div>
            {activeTab === 'projects' && (
              <button onClick={openAdd} className="btn-primary inline-flex items-center gap-2 text-sm">
                <FiPlus /> Add Project
              </button>
            )}
            {activeTab === 'messages' && (
              <button
                onClick={loadMessages}
                disabled={loadingMessages}
                className="btn-outline inline-flex items-center gap-2 text-sm py-2 px-5"
              >
                <FiRefreshCw size={14} className={loadingMessages ? 'animate-spin' : ''} />
                Refresh
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {stats.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="glass-card p-6 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-purple-400 flex-shrink-0"
                  style={{ background: 'rgba(127,4,155,0.2)' }}>
                  {s.icon}
                </div>
                <div>
                  <p className="font-display text-3xl font-normal text-white">{s.value}</p>
                  <p className="font-body text-xs text-white/40">{s.label}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* PROJECTS TAB */}
          {activeTab === 'projects' && (
            <div className="glass-card overflow-hidden">
              <div className="p-6 border-b border-white/5 flex items-center justify-between">
                <h2 className="font-display text-xl font-normal text-white">All Projects</h2>
                <span className="font-mono text-xs text-purple-400">{projects.length} total</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      {['Project', 'Category', 'Technologies', 'Featured', 'Actions'].map(h => (
                        <th key={h} className="px-6 py-4 text-left font-body text-xs text-white/30 tracking-widest uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p, i) => (
                      <motion.tr key={p.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.04 }}
                        className="border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-200">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img src={p.thumbnail} alt={p.title} className="w-10 h-10 rounded-lg object-cover" loading="lazy" />
                            <div>
                              <p className="font-body text-sm text-white font-medium">{p.title}</p>
                              {p.liveUrl && (
                                <a href={p.liveUrl} target="_blank" rel="noopener noreferrer"
                                  className="font-body text-xs text-purple-400 flex items-center gap-1 mt-0.5 hover:text-purple-300 transition-colors">
                                  <FiExternalLink size={10} /> Live
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2.5 py-1 rounded-full text-xs font-body"
                            style={{ background: 'rgba(127,4,155,0.2)', color: '#c084fc' }}>
                            {p.category}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1 flex-wrap max-w-xs">
                            {p.technologies.slice(0, 3).map(t => (
                              <span key={t} className="px-2 py-0.5 rounded text-xs font-mono text-white/50 bg-white/5">{t}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button onClick={() => updateProject(p.id, { featured: !p.featured })}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                              p.featured ? 'text-yellow-400 bg-yellow-400/15' : 'text-white/20 hover:text-white/40 bg-white/5'
                            }`}>
                            <FiStar size={14} className={p.featured ? 'fill-yellow-400' : ''} />
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => openEdit(p)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200">
                              <FiEdit2 size={14} />
                            </button>
                            {deleteConfirm === p.id ? (
                              <div className="flex gap-1">
                                <button onClick={() => { deleteProject(p.id); setDeleteConfirm(null); }}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-all">
                                  <FiCheck size={14} />
                                </button>
                                <button onClick={() => setDeleteConfirm(null)}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:bg-white/10 transition-all">
                                  <FiX size={14} />
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => setDeleteConfirm(p.id)}
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200">
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* MESSAGES TAB */}
          {activeTab === 'messages' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Messages list */}
              <div className="glass-card overflow-hidden">
                <div className="p-5 border-b border-white/5 flex items-center justify-between">
                  <h2 className="font-display text-xl font-normal text-white">Inbox</h2>
                  <span className="font-mono text-xs text-purple-400">{messages.length} messages</span>
                </div>
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                    <FiInbox size={36} className="text-white/20 mb-4" />
                    <p className="font-body text-white/40 text-sm">No messages yet</p>
                    <p className="font-body text-white/25 text-xs mt-1">Messages submitted via the contact form will appear here</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
                    {messages.map((msg, i) => (
                      <motion.button
                        key={msg.id || i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        onClick={() => setSelectedMessage(msg)}
                        className={`w-full text-left p-5 hover:bg-white/[0.03] transition-colors duration-200 ${
                          selectedMessage?.id === msg.id ? 'bg-purple-500/10 border-l-2 border-l-purple-500' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-body text-sm font-semibold text-white truncate">{msg.name}</p>
                          <span className="font-mono text-xs text-white/30 flex-shrink-0">
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleDateString() : 'Recent'}
                          </span>
                        </div>
                        <p className="font-body text-xs text-purple-400 mb-1">{msg.email}</p>
                        <p className="font-body text-xs text-white/40 truncate">{msg.message}</p>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>

              {/* Message detail */}
              <div className="glass-card overflow-hidden">
                {selectedMessage ? (
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h3 className="font-display text-2xl font-normal text-white">{selectedMessage.name}</h3>
                        <a href={`mailto:${selectedMessage.email}`}
                          className="font-body text-sm text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1.5 mt-1">
                          <FiMail size={12} /> {selectedMessage.email}
                        </a>
                      </div>
                      <button onClick={() => setSelectedMessage(null)}
                        className="text-white/30 hover:text-white transition-colors">
                        <FiX size={18} />
                      </button>
                    </div>

                    <div className="space-y-4 mb-6">
                      {selectedMessage.service && selectedMessage.service !== 'Not specified' && (
                        <div className="flex items-center gap-3">
                          <FiTag size={14} className="text-purple-400 flex-shrink-0" />
                          <div>
                            <p className="font-body text-xs text-white/30 uppercase tracking-wider">Service</p>
                            <p className="font-body text-sm text-white">{selectedMessage.service}</p>
                          </div>
                        </div>
                      )}
                      {selectedMessage.budget && selectedMessage.budget !== 'Not specified' && (
                        <div className="flex items-center gap-3">
                          <FiDollarSign size={14} className="text-purple-400 flex-shrink-0" />
                          <div>
                            <p className="font-body text-xs text-white/30 uppercase tracking-wider">Budget</p>
                            <p className="font-body text-sm text-white">{selectedMessage.budget}</p>
                          </div>
                        </div>
                      )}
                      {selectedMessage.timestamp && (
                        <div className="flex items-center gap-3">
                          <FiCalendar size={14} className="text-purple-400 flex-shrink-0" />
                          <div>
                            <p className="font-body text-xs text-white/30 uppercase tracking-wider">Received</p>
                            <p className="font-body text-sm text-white">
                              {new Date(selectedMessage.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-white/8 pt-5">
                      <p className="font-body text-xs text-white/30 uppercase tracking-wider mb-3">Message</p>
                      <p className="font-body text-sm text-white/75 leading-relaxed whitespace-pre-wrap">
                        {selectedMessage.message}
                      </p>
                    </div>

                    <div className="mt-6 pt-5 border-t border-white/8">
                      <a
                        href={`mailto:${selectedMessage.email}?subject=Re: Your  SitePixel Agency Inquiry`}
                        className="btn-primary inline-flex items-center gap-2 text-sm py-2.5"
                      >
                        <FiMail size={14} /> Reply via Email
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-16 text-center px-6">
                    <FiMail size={36} className="text-white/15 mb-4" />
                    <p className="font-body text-white/30 text-sm">Select a message to read</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Project Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)' }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ ease: [0.25, 0.46, 0.45, 0.94] }}
              className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-display text-3xl font-normal text-white">
                  {editingProject ? 'Edit Project' : 'Add New Project'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-white/40 hover:text-white transition-colors">
                  <FiX size={20} />
                </button>
              </div>

              <div className="space-y-5">
                {[
                  { label: 'Project Title *', key: 'title', placeholder: 'Velorum E-Commerce Platform' },
                  { label: 'Thumbnail URL', key: 'thumbnail', placeholder: 'https://images.unsplash.com/...' },
                  { label: 'Live Site URL', key: 'liveUrl', placeholder: 'https://example.com' },
                  { label: 'GitHub URL', key: 'githubUrl', placeholder: 'https://github.com/...' },
                  { label: 'Technologies (comma-separated)', key: 'technologies', placeholder: 'React, Node.js, MongoDB' },
                  { label: 'Features (comma-separated)', key: 'features', placeholder: 'Real-time updates, Payment integration' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="font-body text-xs text-white/40 uppercase tracking-widest mb-2 block">{field.label}</label>
                    <input
                      type="text"
                      value={form[field.key] || ''}
                      onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                      placeholder={field.placeholder}
                      className="input-field"
                    />
                  </div>
                ))}

                <div>
                  <label className="font-body text-xs text-white/40 uppercase tracking-widest mb-2 block">Description</label>
                  <textarea
                    rows={3}
                    value={form.description || ''}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Project description..."
                    className="input-field resize-none"
                  />
                </div>

                <div>
                  <label className="font-body text-xs text-white/40 uppercase tracking-widest mb-2 block">Category</label>
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="input-field"
                    style={{ background: 'rgba(13,8,16,0.9)' }}
                  >
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={() => setForm({ ...form, featured: !form.featured })}
                    className={`w-12 h-6 rounded-full transition-colors duration-250 flex items-center px-1 cursor-pointer ${form.featured ? 'bg-purple-500' : 'bg-white/10'}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform duration-250 shadow-sm ${form.featured ? 'translate-x-6' : ''}`} />
                  </div>
                  <span className="font-body text-sm text-white/60">Mark as Featured</span>
                </label>

                <div className="flex gap-4 pt-4">
                  <button onClick={handleSave} className="btn-primary inline-flex items-center gap-2 flex-1 justify-center">
                    <FiCheck /> {editingProject ? 'Save Changes' : 'Add Project'}
                  </button>
                  <button onClick={() => setShowModal(false)} className="btn-outline flex-1">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
